import React, { useState, useEffect, useCallback, useRef } from 'react';
import queryString from 'query-string';
import { datadogRum } from '@datadog/browser-rum';
import {
  withApi,
  formats,
  useToggle,
  usePolicy,
  useAuth0,
  customTypes,
  useResponsive,
} from 'utils';
import LinkPolicy from 'components/layout/userProfile/LinkPolicy';

import {
  Typography,
  Button,
  SelectPolicy,
  Spinner,
  showErrorAlert,
  Icon,
} from '../alf-design';
import {
  AgencyDetails,
  Billing,
  Claims,
  MakePayment,
  PaymentHistory,
  PolicyDetails,
  PolicyDownloads,
} from './sections';
import AdminPage from './AdminPage';
import { useLocation, useNavigate } from 'react-router-dom';
import { LegacyColors } from 'constants';
import FallbackContent from 'components/FallbackContent';

const styles = {
  circularProgress: {
    display: 'flex',
    justifyContent: 'center',
  },
};

const Dashboard = ({ api }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { screenIsDown } = useResponsive();
  const [queryParams, setQueryParams] = useState(null);
  const [showHistory, toggleHistory] = useToggle(false);
  const [showPayment, togglePayment] = useState(false);
  const [showLinkPolicy, toggleLinkPolicy] = useState(false);
  const [showDownloads, toggleDownloads] = useToggle(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [openSelectPolicy, setOpenSelectPolicy] = useState(false);
  const dashboardPolicyTriggerRef = useRef(null);
  const { user } = useAuth0();
  const isAdmin = user
    ? user?.email?.includes('@slideinsurance.com') && user?.role?.isAdmin
    : false;
  const {
    policies,
    claims,
    selectedPolicy,
    selectedPolicyFusionNH,
    claimsLoading,
    loading,
    error,
    fetchPolicy,
  } = usePolicy({
    loadPolicies: isAdmin ? false : true,
  });
  const hasAgent =
    selectedPolicy?.agent?.organizationName !== '' ||
    selectedPolicy?.agent?.street !== '';

  const handleChangePolicy = useCallback(
    (policyNumber) => {
      const route = `/?${queryString.stringify({
        ...queryParams,
        policy: policyNumber,
      })}`;

      if (queryParams.policy) {
        navigate(route);
      } else {
        navigate(route, { replace: true });
      }
    },
    [queryParams],
  );

  const errorDescription = (err, request) => {
    let description;
    if (err?.response?.statusText) {
      description = err?.response?.statusText;
    } else if (err?.request) {
      description =
        'Network error. Please check your connection and try again.';
    } else {
      description = `Error attempting to fetch your ${request}. Refresh page to try again.`;
    }
    return description;
  };

  useEffect(() => {
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Policy access is designed with you in mind. View your policy details, make payments, access your agent info, and more!',
      );
    }
  }, []);

  useEffect(() => {
    setQueryParams(queryString.parse(location.search));
    document.title = 'Online Policy Access for Slide Insurance Policyholders';
  }, [location.search]);

  useEffect(() => {
    if (error) {
      showErrorAlert({
        message: `${
          error?.entity?.charAt(0).toUpperCase() + error?.entity?.slice(1)
        } Error`,
        description: errorDescription(error?.err, error?.entity),
      });

      if (error?.entity === 'policy') {
        datadogRum.addError(error?.err, {
          event_name: 'account.policy_access.failed',
          operation: 'load_policy',
          http_status: error?.err?.response?.status,
          failure_reason:
            error?.err?.response?.status === 404
              ? 'policy_not_found'
              : error?.err?.response
                ? 'api_error'
                : error?.err?.request
                  ? 'network_error'
                  : 'unexpected_error',
        });
      }

      if (error?.err?.response && error?.err?.response?.status === 404) {
        navigate('/', { replace: true });
      }
    }
  }, [error]);

  useEffect(() => {
    if (queryParams && !queryParams.policy && policies && policies.length > 0) {
      handleChangePolicy(policies[0].policyNumber);
    }
    if (error && error.entity === 'policy' && policies && policies.length > 0) {
      navigate(`/?policy=${policies[0].policyNumber}`, { replace: true });
    }
  }, [queryParams, policies, handleChangePolicy, selectedPolicy, error]);

  useEffect(() => {
    if (queryParams && queryParams.errorMessage) {
      showErrorAlert({
        message: 'Manage Payment Error',
        description: queryParams.errorMessage,
      });
      navigate(
        `/?${queryString.stringify({
          ...queryParams,
          errorMessage: undefined,
        })}`,
        { replace: true },
      );
    }
  }, [queryParams]);

  if (
    isAdmin &&
    !loading &&
    selectedPolicy === null &&
    (!Array.isArray(policies) || policies.length === 0)
  ) {
    return <AdminPage isSearchView />;
  }

  if (
    !isAdmin &&
    !loading &&
    selectedPolicy === null &&
    (!Array.isArray(policies) || policies.length === 0)
  ) {
    return (
      <FallbackContent
        onRecover={(path) => navigate(path, { replace: true })}
      />
    );
  }

  const handleSelectPolicy = (policy) => {
    handleChangePolicy(policy.policyNumber);
    setOpenSelectPolicy(false);
  };

  return !loading && selectedPolicy !== null ? (
    <div className="mt-0">
      <Typography type="h1" component="h1" hidden>
        Dashboard
      </Typography>
      <div className="flex flex-col sm:flex-row justify-between mb-[24px]">
        <div className="relative">
          <div
            ref={dashboardPolicyTriggerRef}
            className="flex items-center text-legacy-primary text-[14px] hover:opacity-80"
          >
            <Typography
              id="dashboard-heading-policy"
              type="h1"
              component="h1"
              className={`mr-[4px] ${policies && policies.length > 1 && 'cursor-pointer'}`}
              onClick={
                policies && policies.length > 1
                  ? () => setOpenSelectPolicy(!openSelectPolicy)
                  : null
              }
            >
              {formats.address(selectedPolicy.property)}
            </Typography>
            {policies && policies.length > 1 && (
              <Icon
                id="selectPolicy"
                ariaHidden
                name={!openSelectPolicy ? 'chevronDown' : 'chevronUp'}
                color={LegacyColors.textDark}
                fontSize={10}
                style={{ verticalAlign: 'middle', marginLeft: 8, marginTop: 0 }}
                onClick={() => setOpenSelectPolicy(!openSelectPolicy)}
              />
            )}
          </div>
          {policies && policies.length > 1 && (
            <SelectPolicy
              id="dashboard-select-policy"
              policies={policies}
              openSelectPolicy={openSelectPolicy}
              onSelect={handleSelectPolicy}
              onClose={() => setOpenSelectPolicy(false)}
              triggerRef={dashboardPolicyTriggerRef}
              listClassName="!top-[96%]"
            />
          )}
        </div>
        <Button
          id="dashboard-button-add-policy"
          data-dd-action-name="dashboard.policy.add_existing"
          onClick={() => toggleLinkPolicy(true)}
          disabled={isAdmin}
          type="secondary"
        >
          Add Existing Policy
        </Button>
      </div>
      <div
        className={`grid gap-[24px] mb-[24px] ${screenIsDown('md2') ? 'grid-cols-1' : 'grid-cols-2'}`}
      >
        <div className="min-w-0">
          <PolicyDetails
            policy={selectedPolicy}
            selectedPolicyFusionNH={
              selectedPolicy?.isFusion ? selectedPolicyFusionNH : null
            }
            toggleDownloads={toggleDownloads}
          />
        </div>
        <div className="min-w-0">
          <Billing
            policy={selectedPolicy}
            policyFusionNH={selectedPolicyFusionNH}
            handleHistoryClick={() => toggleHistory(true)}
            handleMakePaymentClick={() => togglePayment(true)}
            loading={billingLoading}
          />
          {hasAgent && (
            <div className="mt-[24px]">
              <AgencyDetails policy={selectedPolicy} />
            </div>
          )}
        </div>
      </div>
      <Claims claims={claims} loading={claimsLoading} error={error} />
      <PaymentHistory
        show={showHistory}
        onClose={() => toggleHistory(false)}
        policyNumber={selectedPolicy.policyNumber}
      />
      <PolicyDownloads
        show={showDownloads}
        onClose={() => toggleDownloads(false)}
        policyNumber={selectedPolicy?.policyNumber}
        policyFusionNH={selectedPolicyFusionNH?.policyNumber}
        policySystem={selectedPolicy?.policySystem}
        isFusion={selectedPolicy?.isFusion}
      />
      {selectedPolicy?.billing && (
        <MakePayment
          show={showPayment}
          onClose={() => togglePayment(false)}
          selectedPolicy={{
            ...selectedPolicy,
            minAmountDue:
              selectedPolicy?.billing?.pastDueAmount > 0
                ? selectedPolicy?.billing?.pastDueAmount
                : selectedPolicy?.billing?.currentDueAmount,
          }}
          setBillingLoading={setBillingLoading}
          getPolicy={fetchPolicy}
        />
      )}
      <LinkPolicy
        show={showLinkPolicy}
        onClose={() => toggleLinkPolicy(false)}
        api={api}
        fromDashboard={showLinkPolicy}
      />
    </div>
  ) : (
    <div style={styles.circularProgress}>
      {loading && <Spinner id="dashboard-spinner" size={80} />}
    </div>
  );
};

Dashboard.propTypes = {
  api: customTypes.api,
};
export default withApi(Dashboard);
